/* eslint-disable complexity */
/**
 * @memberof firebird
 * @ngdoc controller
 * @name firebirdCtrl
 * @scope
 * @description
 *   Main controller for entire application
 * @param $scope {service}
 *   Controller scope
 * @param $location {service}
 *   Parses the URL in the browser address bar
 * @param $http {service}
 *   Built-in Angular service for managing HTTP requests
 * @param routerService {service}
 *   Service for handling Angular state routing
 * @param helperService {service}
 *   Contains helper functions that can be used application-wide
 * @param vertoService {service}
 *   Service for initialization/starting/stopping verto calls
 * @param meetingAPIService {service}
 *   Service for making API calls to the /meeting endpoint
 * @param screenShareService {service}
 *   Service for initializing/starting/stopping screenshare
 * @param meetingService {service}
 *   Service for maintaining current meeting information
 * @param attendeeService {service}
 *   Service for maintaining current attendee information
 * @param socketService {service}
 *   Service for communicating with socket
 * @param phoneConferenceService {service}
 *   Service for maintaining current phone conference data
 * @param config {service}
 *   Used for accessing application-wide constants
 * @param handRaiseService {service}
 *   Service for maintaining raised hands
 * @attribute {boolean} sidebarToggle
 *   Determines state of left/right sidebars
 * @attribute {string} meetingIdentifier
 *   9-Digit number or Shortcut URL retrieved from URL
 * @attribute {boolean} hasWebRTC
 *   Value based on WebRTC-capability of current browser
 * @attribute {function} initActiveMeeting
 *   Makes initActiveMeeting() publicly accessible
 * @attribute {string} chromeScreenShareExtensionURL
 *   Value of Chrome Screenshare Extension URL (depends on environment)
 */

firebird.controller('firebirdCtrl', [
    '$window',
    '$scope',
    '$rootScope',
    '$q',
    '$timeout',
    '$location',
    '$http',
    '$interval',
    'cookieService',
    'config',
    'logService',
    'routerService',
    'helperService',
    'vertoService',
    'meetingAPIService',
    'screenShareService',
    'meetingService',
    'configService',
    'attendeeService',
    'socketService',
    'phoneConferenceService',
    'analyticsWrapperService',
    'mobileVersionAPIService',
    'connectivityService',
    'storageService',
    'capabilityService',
    'notificationService',
    'inputService',
    'supportLinkService',
    'contentService',
    'questionService',
    'pollingService',
    'camaroService',
    'annotationService',
    'mobileAVDeviceService',
    'localizationService',
    'brandingService',
    'msgFlowService',
    'appInsightsService',
    'videoroomService',
    'promptService',
    'videoFilterService',
    'attendeeListService',
    'uniteRoomsAppNotificationService',
    'uniteRoomsDeviceSelectionService',
    'themedFeaturesService',
    'uniteNotificationService',
    'recordingService',
    'handRaiseService',
    'userPairStateService',
    'vdiService',
    'meetingStateService',
    'executionEnvironmentEnum',
    'automationService',
    'waitingRoomStateService',
    'vdiSdkFactoryService',
    'compatibilityService',
    'authService',
    'vdiStateService',
    function (
        $window,
        $scope,
        $rootScope,
        $q,
        $timeout,
        $location,
        $http,
        $interval,
        cookieService,
        config,
        logService,
        routerService,
        helperService,
        vertoService,
        meetingAPIService,
        screenShareService,
        meetingService,
        configService,
        attendeeService,
        socketService,
        phoneConferenceService,
        analyticsWrapperService,
        mobileVersionAPIService,
        connectivityService,
        storageService,
        capabilityService,
        notificationService,
        inputService,
        supportLinkService,
        contentService,
        questionService,
        pollingService,
        camaroService,
        annotationService,
        mobileAVDeviceService,
        localizationService,
        brandingService,
        msgFlowService,
        appInsightsService,
        videoroomService,
        promptService,
        videoFilterService,
        attendeeListService,
        uniteRoomsAppNotificationService,
        uniteRoomsDeviceSelectionService,
        themedFeaturesService,
        uniteNotificationService,
        recordingService,
        handRaiseService,
        userPairStateService,
        vdiService,
        meetingStateService,
        executionEnvironmentEnum,
        automationService,
        waitingRoomStateService,
        vdiSdkFactoryService,
        // do not remove this, since otherwise this service won't work, it has to be plugged in somewhere
        compatibilityService,
        authService,
        vdiStateService
    ) {
        var self = this;

        const logger = logService.createLoggerWithSource('firebirdCtrl');

        self.sidebarToggle = { left: true, right: false };
        self.overFlowHidden = false;
        self.meetingIdentifier = helperService.getMeetingIdentifier($location.absUrl());
        self.hasWebRTC = helperService.browserHasWebRTC();
        self.initActiveMeeting = initActiveMeeting;
        self.chromeScreenShareExtensionURL = '';
        self.isMobile = helperService.isMobile();
        self.isTablet = false;
        self.isSfuEnabled = false;
        self.audioNacks = false;
        self.isRedesign = null;
        self.isCompactMode = false;
        self.compactModeAvailable = meetingService.getCompactModeAvailable();
        self.hasRedesignFooter = true;
        self.shouldShowVDITitlebar = false;
        self.shouldShowMeetingTitle = false;
        self.enableOracleLiveChat = helperService.isVideoRoomApplication() ? false : true;
        self.enableMarqueeButtonHide = false;
        self.enableCopyFirepadText = true;
        self.isFullScreen = false;

        self.getBeforeUnloadMessage = getBeforeUnloadMessage;
        self.addOnBeforeUnloadConfirmation = addOnBeforeUnloadConfirmation;
        self.removeOnBeforeUnloadConfirmation = removeOnBeforeUnloadConfirmation;
        self.addAnnotationCapabilityListener = addAnnotationCapabilityListener;

        self.hasInitialPayload = false;
        self.retrieveInitialPayload = retrieveInitialPayload;

        self.getMetaDataLink = getMetaDataLink;

        self.getDomainForCookiePersistence = getDomainForCookiePersistence;
        self.onSessionCompleted = onSessionCompleted;

        self.bindKeyPress = bindKeyPress;
        self.allowSlidesKeyPress = allowSlidesKeyPress;

        self._onWindowError = onWindowError;
        self.endMeeting = endMeeting;

        self.imagePath = '';
        self.cacheBuster = Date.now();
        self.metadataLink = undefined;

        self.shouldUseCamaro = helperService.shouldUseCamaro();
        self.initMobileRedirect = initMobileRedirect;
        self.shouldUseLogRocket = shouldUseLogRocket;

        self.useBrandingUB = brandingService.isUB();
        self.getAppLogoURL = meetingService.getAppLogoURL;
        self.isVideoRoomApplication = helperService.isVideoRoomApplication();
        self.showRedirectionNotSupportedPopup = showRedirectionNotSupportedPopup;
        self.VDIRef = undefined;
        self.vdiType = undefined;

        self.showTestrtc = false;

        self.isTitleBarAvailable = isTitleBarAvailable;
        self.getTitleBarTitle = getTitleBarTitle;
        self.disableCompactMode = disableCompactMode;
        self.windowIsResized = windowIsResized;
        self.processOldAuth = processOldAuth;
        self.onShowHideTestrtc = onShowHideTestrtc;

        $scope.$on('node-reconnected', onNodeReconnected);
        $scope.$on('meeting-full', onMeetingFull);
        $scope.$on('meeting-locked-v2', onMeetingLocked);
        $scope.$on('join-request-declined', onJoinRequestDeclined);
        $scope.$on('user-already-exists', onUserAlreadyExists);
        $scope.$on('meeting-state-v2', onMeetingStateV2Updated);
        $scope.$on('attendee-kicked-v2', onAttendeeKickedV2);
        $scope.$on('host-inactive', onHostInactive);
        $scope.$on('host-active', onHostActive);
        $scope.$on('only-one-web-attendee', onOnlyOneWebAttendee);
        $scope.$on('reset-only-one-web-attendee', onResetOnlyOneWebAttendee);
        $scope.$on('session-completed', onSessionCompleted);
        $scope.$on('toggle-left-sidebar', onToggleLeftSidebar);
        $scope.$on('updated-meeting-sfupayload', onUpdatedMeetingSfupayload);
        $scope.$on('showhide-testrtc', self.onShowHideTestrtc);

        $rootScope.$on('removeOnBeforeUnloadConfirmation', onRemoveOnBeforeUnloadConfirmation);
        $rootScope.$on('addOnBeforeUnloadConfirmation', onAddOnBeforeUnloadConfirmation);
        $rootScope.$on('$stateChangeStart', onStateChangeStart);
        $rootScope.$on('$stateChangeSuccess', onStateChangeSuccess);
        $rootScope.$on('use-redesign', onRedesignReceived);
        $rootScope.$on('show-modal', onShowModal);
        $rootScope.$on('enable-compact-mode', onCompactModeReceived);
        $rootScope.$on('show-swal', onShowSwal);

        if (!self.isMobile) {
            // Detect ScreenShare Plugin
            $window.addEventListener('message', function (event) {
                if (event.origin != $window.location.origin) return;

                // Chrome: Detect ScreenShare Plugin
                if (event.data.type && event.data.type === 'SS_PING') {
                    logger.info('Chrome ScreenShare extension detected...');
                    screenShareService.setExtensionInstalled(true);

                    if (screenShareService.isWaitingForInstall()) {
                        screenShareService.endWaitForInstall();
                    }
                }
            });
        }

        if (helperService.isUniteApplication() && $window.preloadedApi.windowManagement.isFullScreen) {
            $window.addEventListener('resize', () => {
                $window.preloadedApi.windowManagement.isFullScreen().then(isFullScreen => {
                    self.isFullScreen = isFullScreen;
                });
            });
        }

        init();

        ////////////////////////

        function onNodeReconnected() {
            logger.info('#onNodeReconnected: retrieving recording status');
            recordingService.queryRecordingStatus(attendeeService.getAttendantId());
        }

        function camaroBeforeUnloadHandler(event) {
            const preventClose =
                !camaroService.getIsUnloading() && (camaroService.getIsClosing() || camaroService.getIsNavigating());
            if (preventClose) {
                event.returnValue = false;
            }
        }

        function addOnBeforeUnloadConfirmation() {
            const onCamaroNavigateListner = function (ev, url) {
                logger.info('#addOnBeforeUnloadConfirmation: calling onCamaroNavigateListner ...');
                camaroService.setIsNavigating(true);
                camaroService.setNavigationUrl(url);
                $rootScope.$broadcast('show-modal', 'camaroLeaveSelection');
            };

            const onCloseWindowListner = function () {
                logger.info('#onCloseWindowListner: calling onCloseWindowListner ...');
                camaroService.setIsClosing();

                if (camaroService.getIsUnloading()) {
                    return;
                }
                $rootScope.$broadcast('show-modal', 'camaroLeaveSelection');
            };

            //for camaro app ... the currentWindow close event is fired before the onbefore unload
            //flags are set in the service because these are needed/set later by the leave dialog
            if (helperService.isElectron()) {
                if (!helperService.isVideoRoomApplication()) {
                    $window.addEventListener('beforeunload', camaroBeforeUnloadHandler);

                    $window.preloadedApi.remote.onCurrentWindowClose(onCloseWindowListner);

                    if (!helperService.isUniteApplication()) {
                        $window.preloadedApi.camaroEvents.onCamaroNavigate(onCamaroNavigateListner);
                    }
                }
            } else if (self.isMobile !== true) {
                // Wrapped this in a function to make it unit testable.
                // Not sure why this caused it to work. Would have thought it was
                // equivalent to $window.onbeforeunload = self.getBeforeUnloadMessage;
                $window.onbeforeunload = function () {
                    uniteNotificationService.sendMeetingLeftEvent();

                    helperService.setIsPageUnloading(true);
                    return self.getBeforeUnloadMessage();
                };
            }
        }

        function removeOnBeforeUnloadConfirmation() {
            if (self.isMobile !== true) {
                if (helperService.isElectron()) {
                    $window.removeEventListener('beforeunload', camaroBeforeUnloadHandler);
                } else {
                    $window.onbeforeunload = undefined;
                }
            }
        }

        /**
         * @memberof firebirdCtrl
         * @function init
         * @description
         *   PRIVATE:
         *
         *   If browser does not have WebRTC enabled, this calls routerService to set 'not supported' viewstate.
         *   Otherwise, this initializes the meeting by:
         *   1. Setting mobile-friendly CSS if needed.
         *   2. Setting host auth token if needed.
         *   3. Setting initial viewstate (active meeting, upcoming, past or not found).
         *   4. Checking if screenshare plugin is installed or not.
         */
        function init() {
            setLocalization();
            $window.onerror = self._onWindowError;

            self.imagePath = brandingService.getFavIconSubPath();
            digest();

            if (config && config.version) {
                logger.info(`version: ${config.version}`);
            }

            setUserCacheFromQueryString();
            if (helperService.isVideoRoomApplication()) {
                uniteRoomsAppNotificationService.init();
                uniteRoomsDeviceSelectionService.init();
            } else {
                notificationService.overrideSwal();
            }

            self.bindKeyPress();

            // Subscibe Unite events
            addUniteListeners();

            addAnnotationCapabilityListener();

            setUpToolTipClassForLogRocket();

            //Instantiate ClipboardJS
            var clipboard = new $window.ClipboardJS('.clipboard-element');

            if (self.isMobile === true) {
                //getAppVersion (and other Cordova plugins) aren't available until after deviceready event
                $window.document.addEventListener('deviceready', function () {
                    self.isTablet = helperService.isTablet();

                    if (self.isTablet) {
                        var sideBarIsVisible =
                            localStorage.getItem('menu-visible') === null ||
                            localStorage.getItem('menu-visible') === 'true';
                        self.sidebarToggle.left = sideBarIsVisible ? true : false;
                    }

                    mobileAVDeviceService.initHeadsetDetection();
                    mobileAVDeviceService.checkBuiltInAudioCapabilities();

                    $window.cordova.getAppVersion
                        .getPackageName()
                        .then(function (packageName) {
                            helperService.mobileAppPackageName = packageName;
                            var notificationTitle = 'AnyMeeting';

                            if (packageName === 'net.serverdata.onlinemeeting') {
                                helperService.setMobileLocation('gomeet.com');
                                notificationTitle = 'Online Meeting';
                            }

                            if ($window.cordova && helperService.getDevicePlatform() != 'iOS') {
                                $window.cordova.plugins.backgroundMode.setDefaults({
                                    title: notificationTitle,
                                    text: 'Meeting in session',
                                    icon: 'notification',
                                    hidden: false
                                });
                            }
                        })
                        .then($window.cordova.getAppVersion.getVersionNumber)
                        .then(
                            function (version) {
                                logger.info(`Successfully retrieved app version: ${version}`);

                                var deviceOS = helperService.getDevicePlatform();
                                var resellerType = 'direct';

                                if (helperService.mobileAppPackageName === 'net.serverdata.onlinemeeting') {
                                    resellerType = 'plr';
                                }

                                var appUpdateParams = {
                                    version: version,
                                    deviceOS: deviceOS,
                                    reseller: resellerType
                                };

                                mobileVersionAPIService.checkForAppUpdates(appUpdateParams).then(
                                    function (appUpdateStatus) {
                                        logger.info(`Successfully retrieved app updates: ${appUpdateStatus}`);
                                        routerService.setMobileView(appUpdateStatus);
                                    },
                                    function (err) {
                                        logger.error('#init: Error checking for app updates', err);
                                        routerService.setMobileView(false);
                                    }
                                );
                            },
                            function (err) {
                                logger.error('#init: Error retrieving current app version', err);
                                routerService.setMobileView(false);
                            }
                        );
                });
            } else {
                analyticsWrapperService.initNonMobileAnalytics({
                    isPLR: brandingService.isPLR() || helperService.getQueryParameter('isPLR') === 'true',
                    shouldLoadHeap: false
                });
                $window.onunload = function () {
                    logger.info('window.onunload: exited meeting app');

                    var prop = {};
                    if (attendeeService.getIsHost()) {
                        prop.attendeeCount = attendeeListService.getMaxAttendeeCountReached();
                        prop.attendeeGroup = attendeeListService.getGroupLabelForAttendeeCount(prop.attendeeCount);
                    }
                    analyticsWrapperService.eventTrack('meeting - exited meeting app', prop);
                };

                // Detect Mobile Browser
                if (/Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
                    angular.element('html').addClass('ismobile');
                }

                processAuth()
                    .then(() => meetingAPIService.getUserMetaInfo())
                    .catch(err => logger.warn('#init: there is a problem during getUserMetaInfo', err))
                    .then(data => {
                        if (data?.userIdentity && data?.accountIdentity) {
                            analyticsWrapperService.setPendoInitMetadata(
                                data.userIdentity,
                                data.accountIdentity,
                                localizationService.getLocalization()
                            );
                        }
                        if (
                            helperService.getQueryParameter('enablePendo') === 'true' ||
                            analyticsWrapperService.getEnvironment() === 'prod'
                        ) {
                            analyticsWrapperService.loadPendoAnalytics();
                            analyticsWrapperService.initializePendo();
                        }
                    })
                    .then(() => getIsHost())
                    .then(() => applyLocalization())
                    .catch(err => logger.error(err))
                    .then(() => routerService.resolveInitialPath(self.meetingIdentifier))
                    .then(meeting => {
                        logger.info('Meeting Object: ', meeting);

                        if (helperService.shouldUseMobileRedirect()) {
                            self.initMobileRedirect(meeting);
                            return;
                        }
                        meetingService.init(meeting);

                        updateMeetingCodeInUserCache(meeting);

                        self.chromeScreenShareExtensionURL = screenShareService.getScreenShareExtensionURL('chrome');
                        screenShareService.extensionURL = screenShareService.getScreenShareExtensionURL();

                        //TODO  Eventually we need to retrieve the company name from the DB and initialize/set it somewhere.
                        $window.document.title =
                            meetingService.getCompanyName() +
                            ' Web Conferencing, Video Conferencing and Webinar Software';

                        meetingStateService.meetingCode = meeting.meetingCode;
                        meetingStateService.meetingType = meeting.meetingTypeDisplay;
                        meetingStateService.nodeDomain = meeting.meetingServerURI;

                        analyticsWrapperService.storeMeetingCode(meeting.meetingCode);

                        var meetingType = meetingService.getMeetingType();
                        if (meetingType === 'screenshare' || meetingType === 'videoconference') {
                            if (meetingType === 'videoconference') {
                                attendeeService.setIsVideoMuted(false); //Enable webcams by default
                            }

                            $rootScope.$broadcast('toggle-left-sidebar', 'closed');

                            digest();
                        }

                        analyticsWrapperService.setMeetingType(meetingType);
                    })
                    .catch(function (err) {
                        logger.error('#init: error', err);
                    });
            }
        }

        function setUpToolTipClassForLogRocket() {
            (function ($) {
                if (!$.fn.tooltip || typeof $.fn.tooltip.Constructor === 'undefined') {
                    if (logger && typeof logger.warn === 'function') {
                        logger.warn('Bootstrap Tooltip plugin unavailable; skipping LogRocket tooltip patch');
                    }
                    return;
                }

                var Tooltip = $.fn.tooltip.Constructor;

                $.extend(Tooltip.DEFAULTS, {
                    customClass: ''
                });

                var _show = Tooltip.prototype.show;

                Tooltip.prototype.show = function () {
                    _show.apply(this, Array.prototype.slice.apply(arguments));

                    var $tip = this.tip();
                    $tip.addClass('_lr-hide');

                    if (this.options.customClass) {
                        $tip.addClass(this.options.customClass);
                    }
                };
            })(window.jQuery);
        }

        function setLocalization() {
            var locale = helperService.getQueryParameter('lang') || $window.navigator.language;
            if (locale) {
                localizationService.setLocalization(locale);

                if (locale.toLowerCase() === 'ja-jp') {
                    $window.document.documentElement.lang = 'ja';
                }
            }
        }

        function processAuth() {
            const deferred = $q.defer();
            const isAuthSupervisedEnv = helperService.isElectron() || helperService.isUniteApplication();
            const authFunc = isAuthSupervisedEnv ? authService.processElectronAuth : authService.processAuth;

            self.processOldAuth()
                .then(() => deferred.resolve())
                .catch(() =>
                    authFunc
                        .apply(authService)
                        .then(() => deferred.resolve())
                        .catch(() => deferred.resolve())
                );

            return deferred.promise;
        }

        function processOldAuth() {
            return meetingAPIService.processAuthExchange({ meetingCode: self.meetingIdentifier });
        }

        function getIsHost() {
            var deferred = $q.defer();
            meetingAPIService.getIsHost({ meetingCode: self.meetingIdentifier }).then(
                function (authToken) {
                    analyticsWrapperService.eventTrack('meeting - Host loaded app');

                    attendeeService.setAttendeeType('host');
                    $http.defaults.headers.common.Authorization = 'Bearer ' + authToken;

                    deferred.resolve();
                },
                function (err) {
                    deferred.reject(err + ' Instantiating as attendee.');
                }
            );
            return deferred.promise;
        }

        function applyLocalization() {
            const deferred = $q.defer();
            setTimeout(function () {
                deferred.reject('Failed to apply localization. Timeout elapsed');
            }, 5000);

            localizationService
                .initialized()
                .then(() => deferred.resolve())
                .catch(error => {
                    logger.error('#applyLocalization: apply localization failed', error);
                    deferred.reject('Failed to apply localization');
                });
            return deferred.promise;
        }

        function bindKeyPress() {
            $window.document.addEventListener('keyup', function (e) {
                if (!self.allowSlidesKeyPress()) {
                    return;
                }

                var keyName = '';

                switch (e.keyCode) {
                    case 33: //Page Up (Left Button for Clickers)
                    case 37: //Left Arrow
                        keyName = 'left';
                        break;
                    case 34: //Page Down (Right Button for Clickers)
                    case 39: //Right Arrow
                        keyName = 'right';
                        break;
                }

                if (keyName) {
                    $rootScope.$broadcast('slides-key-press', keyName);
                }
            });
        }

        function allowSlidesKeyPress() {
            if (inputService.isInputFocused()) {
                return false;
            }

            if (contentService.isContentPlaying() && contentService.getContentType() === 'slides') {
                var isContentOwner = contentService.getContentOwner() === attendeeService.getAttendantId();

                if (meetingService.isWebinar()) {
                    //Allow only presenters to advance slides in webinars
                    if (!attendeeService.getIsPresenter()) {
                        return false;
                    }
                } else {
                    //Only content owner can advance slides in meetings
                    if (!isContentOwner) {
                        return false;
                    }
                }
            } else {
                return false;
            }

            if (
                questionService.getIsInQandAMode() ||
                pollingService.getIsVoting() ||
                promptService.isModalActive('webinarPresenterQA') ||
                promptService.isModalActive('polls')
            ) {
                return false;
            }

            return true;
        }

        function onShowHideTestrtc(e, data) {
            if (self.isSfuEnabled && self.isRedesign) {
                logger.log('onShowHideTestrtc value=' + data.value);
                if (!data.value || !self.isCompactMode) {
                    self.showTestrtc = data.value;
                    if (!self.showTestrtc && meetingService.showTestrtc) {
                        meetingService.showTestrtc = false;
                        $rootScope.$broadcast('meeting-active-init');
                    }
                }
                digest();
            } else {
                logger.log(
                    'onShowHideTestrtc ignored as isSfuEnabled=' + self.isSfuEnabled + ' isRedesign=' + self.isRedesign
                );
            }
        }

        /**
         * @memberof firebirdCtrl
         * @function initActiveMeeting
         * @description
         *   PRIVATE:
         *
         *   Inits vertoService and phoneConferenceService by passing phoneInfo
         *   and sets meeting viewstate to 'active'.
         *   Doesn't do anything if phoneInfo is null/empty.
         * @param {object} phoneInfo
         *   Phone info retrieved from server
         */
        function initActiveMeeting(payload) {
            if (!payload || Object.keys(payload).length <= 0) {
                logger.error('#initActiveMeeting: Error initiating active meeting... invalid payload', payload);
                return;
            }

            helperService.removeDeprecatedIceServers(payload.iceServers);
            connectivityService.setIceServers(payload.iceServers && payload.iceServers.slice(0)); // send a copy of array
            meetingService.setFeatureList(payload.featureList);
            vertoService.init(payload.phoneInfo, payload.iceServers);
            automationService.setPhoneConfInfo(payload.phoneInfo);
            phoneConferenceService.init(payload.phoneInfo);
            meetingStateService.phoneInfo = payload.phoneInfo;
            attendeeService.setAudioPin(payload.audioPin);
            if (
                !meetingService.isWebinar() &&
                meetingService.getMeetingSecuritySettings().canUseTheme &&
                themedFeaturesService.isBetweenThemeStartAndEndDate() &&
                localizationService.isEnLocale()
            ) {
                logger.info('Themed Features Available');
                themedFeaturesService.setThemedFeaturesAvailable(true);
            }

            if (payload.isVideoLow) {
                videoroomService.setIsVideoLowFromServer(true);
            }

            if (meetingService.isWebinar() && helperService.notNullOrUndefined(payload.webinarPayload)) {
                meetingService.setIsOnAir(payload.webinarPayload.isOnAir);
                meetingService.setWebinarOptions(payload.webinarPayload.webinarOptions);
                meetingService.setParentMeetingAccessCode(payload.webinarPayload.parentMeetingAccessCode);
                vertoService.setOriginConfUUID(payload.webinarPayload.originConfUUID);
                connectivityService.setFsOriginConnectionProblem(!!payload.isFsOriginConnectionProblem);
            }

            //For mobile, get Mic/Cam permissions before setting active meeting state
            if (self.isMobile) {
                var videoAllowed = false,
                    audioAllowed = false;

                helperService
                    .getMobileMicAuthorization()
                    .then(function (micAuthorized) {
                        logger.info('Mobile Mic Authorized');
                        audioAllowed = true;
                        return;
                    })
                    .catch(function (e) {
                        logger.error('Error retrieving Mobile Mic permissions', e);
                        return;
                    })
                    .then(helperService.getMobileCameraAuthorization)
                    .then(function (webCamAuthorized) {
                        logger.info('Mobile WebCam Authorized');
                        videoAllowed = true;
                        return;
                    })
                    .catch(function (e) {
                        logger.error('Error retrieving Mobile WebCam permissions:', e);
                        return;
                    })
                    .finally(function () {
                        vertoService.setMobilePermissions({
                            audioAllowed: audioAllowed,
                            videoAllowed: videoAllowed
                        });

                        routerService.setActiveMeetingState();
                    });
            } else {
                let showTestrtc = false;
                if (self.isSfuEnabled) {
                    const enableTestrtc = helperService.getQueryParameter('enableTestrtc');
                    if (enableTestrtc === 'start') {
                        showTestrtc = true;
                        meetingService.showTestrtc = true;
                    }
                }

                routerService.setActiveMeetingState();

                if (showTestrtc) {
                    // skip loading the meeting, but load only testrtc.
                    onShowHideTestrtc(null, { value: true });
                }
            }
        }

        function onUpdatedMeetingSfupayload(e, data) {
            logger.info('Meeting SFU payload updated: ', data);
            var payload = configService.getSfuPayload();
            var attributes = [];
            for (var attr in data) {
                payload[attr] = data[attr];
                attributes.push(attr);
            }

            // indicate that settings has changed, and videoroomCtrl needs to restart.
            $rootScope.$broadcast('advanced-settings-changed', attributes);
        }

        /**
         * @memberof firebirdCtrl
         * @function onMeetingStateV2Updated
         * @description
         *   PRIVATE:
         *
         *   Gets meeting state and does one of the following:
         *   1. 'initializing': Currently does not do anything
         *   2. 'running': Retrieves phone info and calls initActiveMeeting().
         *      Also retrives image url from server and sets in attendeeService
         *   3. 'ending': calls endMeeting from meetingService
         * @param {object} e
         *     Event object
         * @param {object} options
         *      properties: {
         *          state: string
         *      }
         *     Possible meeting states: initializing, running, ending
         */
        function onMeetingStateV2Updated(e, options) {
            logger.info(`Meeting State V2 Updated: ${options.state}`);
            connectivityService.setMeetingState(options.state);
            self.shouldShowMeetingTitle = options.state === 'running' || attendeeService.getIsHost();
            switch (options.state) {
                case 'initializing':
                    break;
                case 'running':
                    var handler = function () {
                        if ($window.cordova) {
                            $window.cordova.plugins.backgroundMode.enable();
                        }
                        if (!self.hasInitialPayload) {
                            self.retrieveInitialPayload();
                        }
                        helperService.tryRequestDesktopNotificationPermission();
                    };
                    if (
                        helperService.isElectron() &&
                        $window.preloadedApi?.remote?.ipcRenderer?.getIsVirtualizationEnvironment
                    ) {
                        $window.preloadedApi.remote.ipcRenderer.getIsVirtualizationEnvironment(function (_, data) {
                            logger.info('#getIsVirtualizationEnvironment: ', data);

                            // If Virtualization Enabled, then wait till the VDI SDK Redirection completes
                            checkForVDIRedirection(data, handler);
                        });
                    } else if ($window.navigator.mediaDevices && $window.navigator.mediaDevices.mmrClientVersion) {
                        // Web browser on Azure AVD: mmr_main.js has shimmed mediaDevices (confirmed by mmrClientVersion).
                        logger.debug('#VDI: Azure MMR detected in web', {
                            mmrClientVersion: $window.navigator.mediaDevices.mmrClientVersion,
                            mmrExtensionVersion: $window.navigator.mediaDevices.mmrExtensionVersion,
                            mmrHostVersion: $window.navigator.mediaDevices.mmrHostVersion,
                            isVideoPlaybackRedirectionEnabled:
                                $window.navigator.mediaDevices.isVideoPlaybackRedirectionEnabled
                        });
                        initializeVDIEnvironment(executionEnvironmentEnum.Azure);
                        handler();
                    } else {
                        initializeVDIEnvironment(executionEnvironmentEnum.NonVDI);
                        handler();
                    }
                    break;
                case 'ending':
                    self.endMeeting({ reason: options.reason });
                    break;
                case 'error':
                    routerService.setMeetingErrorState();
                    break;
                default:
            }
        }

        function checkForVDIRedirection(data, callback) {
            let executionEnvironment = executionEnvironmentEnum.NonVDI;
            if (data.executionEnvironment) {
                executionEnvironment = data.executionEnvironment;
            } else if (data.isVirtualizationEnabled) {
                // Older Unite version only handles Citrix and Non-Citrix environment. Hence for backward compatibility we have added below condition.
                executionEnvironment = data.isVirtualizationEnabled
                    ? executionEnvironmentEnum.Citrix
                    : executionEnvironmentEnum.NonVDI;
            }
            logger.debug(`executionEnvironment: ${executionEnvironment}`);
            switch (executionEnvironment) {
                case executionEnvironmentEnum.Citrix:
                case executionEnvironmentEnum.VMware:
                case executionEnvironmentEnum.HorizonOmnissa: {
                    const maxAttempts = 5;
                    let attempts = 0;
                    let cancelInterval = false;
                    let virtualizationCheckInterval = $interval(function () {
                        logger.debug(`Waiting for isVdiClientConnected flag: ${attempts++}`);
                        if ($window.isVdiClientConnected) {
                            initializeVDIEnvironment(executionEnvironment);
                            analyticsWrapperService.eventTrack(`meeting - meeting started in ${executionEnvironment}`);
                            cancelInterval = true;
                            helperService.getVDIRef().initSdkProxy();
                        } else if (attempts > maxAttempts) {
                            logger.warn('#checkForVDIRedirection: Exceeded max attempts for redirection.');
                            initializeVDIEnvironment(executionEnvironmentEnum.NonVDI);
                            analyticsWrapperService.eventTrack(
                                `meeting - meeting started in ${executionEnvironment} without SDK`
                            );
                            $timeout(function () {
                                self.showRedirectionNotSupportedPopup();
                            }, 10000);
                            cancelInterval = true;
                        }
                        if (cancelInterval) {
                            $interval.cancel(virtualizationCheckInterval);
                            virtualizationCheckInterval = null;
                            callback();
                        }
                    }, 5000);
                    break;
                }
                case executionEnvironmentEnum.Azure: {
                    initializeVDIEnvironment(executionEnvironment);
                    analyticsWrapperService.eventTrack(`meeting - meeting started in ${executionEnvironment}`);
                    callback();
                    break;
                }
                case executionEnvironmentEnum.NonVDI:
                default: {
                    // If Unite returns execution environment other than Non-VDI, Citrix or VMware. We will fallback to Non-VDI execution environment
                    initializeVDIEnvironment(executionEnvironmentEnum.NonVDI);
                    callback();
                    break;
                }
            }
        }

        function initializeVDIEnvironment(executionEnvironment) {
            logger.info('#initializeVDIEnvironment: VDI environment initialized: ', executionEnvironment);
            let vdiRef = vdiSdkFactoryService.createVdiService(executionEnvironment, helperService.isElectron());
            // ?stationaryOverlays=true or the Feature Flags panel option exercises the VDI reactions tray
            // (ANYAM-123367) on a non-VDI machine. Clip rects stay no-ops off Horizon. Read like
            // hideMuteIndicator (meetingActiveCtrl) rather than via configService.readStorage, which has side effects.
            if (
                helperService.getQueryParameter('stationaryOverlays') === 'true' ||
                JSON.parse(storageService.get('advancedSettings') || '{}').stationaryOverlays == 'true'
            ) {
                vdiRef.showAnimatedOverlaysOverVideo = false;
            }
            helperService.setVDIRef(vdiRef);
            vdiStateService.VdiSdk = vdiRef;
            $window.Janus.VDIRef = vdiRef;
            $window.Janus.isCitrixSDKVersionFour = helperService.isCitrixSDKVersionFour;
            self.VDIRef = vdiRef;
            self.vdiType = executionEnvironment;
            if (vdiRef && !vdiRef.isE2EESupported) {
                socketService.send({ message: 'attendee-supportse2ee-update', data: { supportsE2EE: false } });
            }
        }

        function endMeeting(options) {
            meetingService.endMeeting(options);
            if (attendeeService.getIsHost()) {
                var prop = {};
                prop.attendeeCount = attendeeListService.getMaxAttendeeCountReached();
                prop.attendeeGroup = attendeeListService.getGroupLabelForAttendeeCount(prop.attendeeCount);
                analyticsWrapperService.eventTrack('meeting - exited meeting app', prop);
            }
            $window.swal.close();
            $window.$('.popover').popover('hide');

            if (self.isMobile) {
                storageService.clearTimedKeys();
            }
        }

        function onAttendeeKickedV2(e, data) {
            logger.info('#onAttendeeKickedV2', data);

            annotationService.clearScreen();

            connectivityService.setServerInitiatedDisconnect(true);
            clearUserData();

            var sharerConnection = annotationService.getSharerConnection();
            if (sharerConnection) {
                sharerConnection.close();
            }

            meetingService.disconnectKickedAttendeeFromMeeting(data);
            $window.swal.close();

            if (self.isMobile) {
                storageService.clearTimedKeys();
            }
        }

        function clearUserData() {
            // when kicked user refreshes meeting page, he will be shown 'Join the meeting' page
            cookieService.remove('userCache');
        }

        function onMeetingFull(e, data) {
            if (data.reason === 'e2ee') {
                routerService.setE2EEMeetingFullState(data);
            } else {
                routerService.setMeetingFullState(data);
            }
        }

        function onMeetingLocked(e, data) {
            routerService.setMeetingLockedState(data);
        }

        function onJoinRequestDeclined(e, data) {
            connectivityService.setServerInitiatedDisconnect(true);
            waitingRoomStateService.joinRequestDeclined = true;
            routerService.setJoinRequestDeclinedState(data);
            var notificationMessage = localizationService.translate(
                'JOIN_REQUEST_LIST.DESKTOP_NOTIFICATIONS.JOIN_REQUEST_DECLINED'
            );
            notificationService.tryShowDesktopNotification(notificationMessage);
        }

        function showJoinRequestAcceptedNotification() {
            var notificationMessage = localizationService.translate(
                'JOIN_REQUEST_LIST.DESKTOP_NOTIFICATIONS.JOIN_REQUEST_ACCEPTED'
            );
            notificationService.tryShowDesktopNotification(notificationMessage);
        }

        function onRedesignReceived(e, data) {
            self.isRedesign = data.isRedesign;
            digest();
        }

        function onCompactModeReceived(e, data) {
            self.isCompactMode = data;
            if (helperService.isUniteApplication()) {
                $window.preloadedApi.remote.ipcRenderer.sendEnableCompactMode(data);
            }
            digest();
        }

        function retrieveInitialPayload() {
            socketService.send({
                message: 'initial-payload',
                data: {},
                callback: function (payload) {
                    if (!payload) {
                        logger.error('retrieveInitialPayload', 'Invalid payload received from server', payload);
                        return;
                    }

                    if (helperService.notNullOrUndefined(payload.attendeeLoginUrl)) {
                        logger.debug('Received Attendee Login Url:', payload.attendeeLoginUrl);
                        meetingService.setAttendeeLoginUrl(payload.attendeeLoginUrl);
                    } else {
                        logger.error('retrieveInitialPayload', 'Initial payload - invalid attendee login url');
                    }

                    if (helperService.notNullOrUndefined(payload.phoneInfo)) {
                        var phoneInfo = JSON.parse(JSON.stringify(payload.phoneInfo));
                        if (!attendeeService.getIsHost()) {
                            delete phoneInfo.presenterPIN;
                        }
                        logger.debug('Received Phone Info:', phoneInfo);
                    } else {
                        logger.error('retrieveInitialPayload', 'Initial payload - invalid phone info');
                    }

                    if (helperService.notNullOrUndefined(payload.gravatarImgURL)) {
                        logger.debug('Received Gravatar Img Url:', payload.gravatarImgURL);
                        attendeeService.setImageURL(payload.gravatarImgURL);
                    } else {
                        logger.error('retrieveInitialPayload', 'Initial payload - invalid gravatar img url');
                    }

                    if (helperService.notNullOrUndefined(payload.calendar)) {
                        logger.debug('Received calendar information');
                        meetingService.setCalendarTimerInfo(payload.calendar);
                    }

                    if (helperService.notNullOrUndefined(payload.featureList)) {
                        logger.debug('Received Feature List:', payload.featureList);
                    } else {
                        logger.error('retrieveInitialPayload', 'Initial payload - feature list');
                    }

                    if (helperService.notNullOrUndefined(payload.iceServers)) {
                        logger.debug('Received Ice Servers:', payload.iceServers);
                    } else {
                        logger.error('retrieveInitialPayload', 'Initial payload - invalid ice servers');
                        payload.iceServers = [];
                    }

                    if (helperService.notNullOrUndefined(payload.joinAudioMuted)) {
                        logger.debug('Received joinAudioMuted:', payload.joinAudioMuted);

                        //TODO: Seems that this logic should be merged with logic in conferenceCtrl.js/getInitialIsAudioMuted
                        if (attendeeService.getIsHost()) {
                            //Sets host to be muted by default
                            attendeeService.setIsAudioMuted(false);
                        } else {
                            //tells attendess to join based off of host setting
                            attendeeService.setIsAudioMuted(payload.joinAudioMuted);
                        }

                        meetingService.setJoinAudioMuted(payload.joinAudioMuted);
                    } else {
                        logger.error('retrieveInitialPayload', 'Initial payload - invalid joinAudioMuted');
                    }

                    if (helperService.notNullOrUndefined(payload.audioPin)) {
                        logger.debug('Received audioPin:', payload.audioPin);
                    } else {
                        logger.error('retrieveInitialPayload', 'Initial payload - invalid audioPin');
                    }

                    if (helperService.notNullOrUndefined(payload.customBranding)) {
                        logger.debug('Received custom branding:', payload.customBranding);
                        meetingService.setBrandingInfo(payload.customBranding);
                    } else {
                        logger.error('retrieveInitialPayload', 'Initial payload - invalid custom branding');
                    }

                    if (helperService.notNullOrUndefined(payload.recordingStatus)) {
                        logger.debug('Received recordingStatus:', payload.recordingStatus);
                        recordingService.updateRecordingStatus(payload.recordingStatus);
                    } else {
                        logger.error('retrieveInitialPayload', 'initial payload - invalid recording status');
                    }

                    if (helperService.notNullOrUndefined(payload.pendoAvailable)) {
                        logger.debug('Received pendoAvailable payload:', payload.pendoAvailable);
                        analyticsWrapperService.setPendoAvailable(payload.pendoAvailable);
                    }

                    if (meetingService.isWebinar()) {
                        if (helperService.notNullOrUndefined(payload.webinarPayload)) {
                            logger.debug('Received Webinar Options:', payload.webinarPayload.webinarOptions);
                            logger.debug("Received 'is On-Air' Status:", payload.webinarPayload.isOnAir);
                            logger.debug('Received originConfUUID:', payload.webinarPayload.originConfUUID);
                        } else {
                            logger.error('retrieveInitialPayload', 'Initial payload - invalid webinar payload');
                        }

                        if (helperService.notNullOrUndefined(payload.isWebinarVirtualBlurredBackgroundAvailable)) {
                            logger.debug(
                                'Received isWebinarVirtualBlurredBackgroundAvailable payload:',
                                payload.isWebinarVirtualBlurredBackgroundAvailable
                            );
                            meetingService.setIsWebinarVirtualBlurredBackgroundAvailable(
                                payload.isWebinarVirtualBlurredBackgroundAvailable
                            );
                            videoFilterService.setIsVideoFiltersAvailable(
                                payload.isWebinarVirtualBlurredBackgroundAvailable
                            );
                        } else {
                            // disable video background in webinar if there is no user pair
                            videoFilterService.setIsVideoFiltersAvailable(false);
                        }

                        if (
                            helperService.notNullOrUndefined(payload.enableOracleLiveChat) &&
                            !helperService.isVideoRoomApplication()
                        ) {
                            logger.debug(
                                'retrieveInitialPayload',
                                'Received enableOracleLiveChat payload: ',
                                payload.enableOracleLiveChat
                            );
                            self.enableOracleLiveChat = payload.enableOracleLiveChat;
                            meetingService.setEnableOracleLiveChat(self.enableOracleLiveChat);
                        }
                    } else {
                        if (helperService.notNullOrUndefined(payload.msgflowService)) {
                            logger.debug('Received msgflowService:', payload.msgflowService);
                            msgFlowService.initialize(payload.msgflowService);
                        } else {
                            logger.error('retrieveInitialPayload', 'Initial payload - invalid msgflowService');
                        }

                        if (helperService.notNullOrUndefined(payload.sfuPayload)) {
                            logger.debug('Received SFU Payload:', payload.sfuPayload);

                            self.isSfuEnabled = true;

                            //This querystring override check is a mechanism to test recording layouts in
                            // freeswitch by connecting as an MCU client to the meeting
                            var sfuEnabled = helperService.getQueryParameter('sfuEnabled');
                            if (sfuEnabled) {
                                // 'true' or 'false'
                                self.isSfuEnabled = sfuEnabled == 'true';
                            }

                            meetingService.setIsSfuEnabled(self.isSfuEnabled);
                            videoFilterService.setIsSfuEnabled(self.isSfuEnabled);
                            // set audionacks only is sfuEnabled
                            var audioNacks = helperService.getQueryParameter('audioNacks');
                            if (audioNacks) {
                                // 'true' or 'false'
                                self.audioNacks = audioNacks == 'true';
                            } else {
                                self.audioNacks = payload.sfuPayload.audioNacks;
                            }

                            meetingService.setAudioNacks(self.audioNacks);

                            if (payload.sfuPayload.videoList && Array.isArray(payload.sfuPayload.videoList)) {
                                logger.debug('Setting initial SFU video list');
                                videoroomService.setVideoList(payload.sfuPayload.videoList);
                            }

                            meetingService.setAttendeeFavoritesEnabled(payload.sfuPayload.layoutPin == 'true');

                            //This is used for the Attendee checkbox to mute themselves when they join a meeting
                            if (!attendeeService.getIsHost()) {
                                //sets default to true
                                var userShouldJoinMuted = true;
                                //gets the value from storage that was set in generalSettingsctrl-- should this be broadcasted rather then just gotten?
                                var joinAudioStatePref = storageService.get('attendeeJoinAudioStatePref');
                                if (joinAudioStatePref !== null) {
                                    userShouldJoinMuted = joinAudioStatePref;
                                } else {
                                    userShouldJoinMuted = true;
                                }
                                attendeeService.setIsAudioMuted(userShouldJoinMuted);
                            }

                            if (payload.sfuPayload) {
                                // for videoFilter, treat its URL param as if user pair was set with same value
                                var videoFilter =
                                    helperService.getQueryParameter('videoFilter') ||
                                    payload.sfuPayload.videoFilter ||
                                    null;
                                if (videoFilter) {
                                    // 'true' or 'false' or other string
                                    payload.sfuPayload.videoFilter = videoFilter == 'false' ? false : videoFilter;
                                }
                                if (payload.sfuPayload.videoFilterConfig) {
                                    videoFilterService.setVideoFilterConfig(payload.sfuPayload.videoFilterConfig);
                                }
                            }

                            if (payload.sfuPayload) {
                                configService.setSfuPayload(payload.sfuPayload);
                            }
                            if (helperService.notNullOrUndefined(payload.sfuPayload.customLogLevel)) {
                                configService.setLogLevelFromConfig();
                            }
                            if (helperService.notNullOrUndefined(payload.sfuPayload.meetingCryptoKeySalt)) {
                                logger.debug(
                                    'Received meetingCryptoKeySalt payload:',
                                    payload.sfuPayload.meetingCryptoKeySalt
                                );
                                meetingService.setMeetingCryptoKeySaltValue(payload.sfuPayload.meetingCryptoKeySalt);
                                if (!helperService.getVDIRef().isE2EESupported) {
                                    $rootScope.$broadcast('attendee-kicked-v2', { reason: 'e2ee-not-supported-vdi' });
                                    return;
                                }
                            }
                        } else {
                            logger.error('retrieveInitialPayload', 'Initial payload - invalid sfu payload');
                        }

                        if (helperService.notNullOrUndefined(payload.deviceSelection)) {
                            logger.debug('Received deviceSelection payload:', payload.deviceSelection);
                            meetingService.setDeviceSelectionEnabled(payload.deviceSelection);
                        }

                        if (helperService.notNullOrUndefined(payload.verticalJoinFlow)) {
                            logger.debug('Received verticalJoinFlow payload:', payload.verticalJoinFlow);
                            meetingService.setVerticalJoinFlowEnabled(payload.verticalJoinFlow);
                        }

                        if (helperService.notNullOrUndefined(payload.joinFlowRedesign)) {
                            logger.debug('Received joinFlowRedesign payload:', payload.joinFlowRedesign);
                            meetingService.setJoinFlowRedesignEnabled(payload.joinFlowRedesign);
                        }

                        // automatically start Virtual assistant when va starts
                        if (helperService.notNullOrUndefined(payload.startVaOnRecording)) {
                            logger.debug('Received startVaOnRecording payload:', payload.startVaOnRecording);
                            meetingService.setStartVaOnRecording(payload.startVaOnRecording);
                        }

                        // Meeting Reactions
                        if (helperService.notNullOrUndefined(payload.meetingReactionsOn)) {
                            logger.debug('Received meetingReactions payload:', payload.meetingReactionsOn);
                            meetingService.setMeetingReactionsOn(payload.meetingReactionsOn);
                        }

                        // Audio prompts for Meeting Lock & Unlock
                        if (helperService.notNullOrUndefined(payload.meetingLockAudioPromptsEnabled)) {
                            logger.debug(
                                'Received meetingLockAudioPromptsEnabled payload:',
                                payload.meetingLockAudioPromptsEnabled
                            );
                            meetingService.setMeetingLockAudioPromptsEnabled(payload.meetingLockAudioPromptsEnabled);
                        }

                        if (helperService.notNullOrUndefined(payload.virtualBackground)) {
                            logger.debug('Received virtualBackground payload:', payload.virtualBackground);
                            meetingService.setVirtualBackgroundEnabled(payload.virtualBackground);
                        }

                        if (helperService.notNullOrUndefined(payload.meetingPrivateChatEnabled)) {
                            logger.debug(
                                'Received meetingPrivateChatEnabled payload:',
                                payload.meetingPrivateChatEnabled
                            );
                            meetingService.setMeetingPrivateChatEnabled(payload.meetingPrivateChatEnabled);
                        }

                        if (helperService.notNullOrUndefined(payload.e2eeAvailable)) {
                            logger.debug('Received e2eeAvailable payload:', payload.e2eeAvailable);
                            meetingService.setE2EEAvailable(payload.e2eeAvailable);
                        }

                        if (helperService.notNullOrUndefined(payload.modalsAvailable)) {
                            logger.debug('Received modalsAvailable payload:', payload.modalsAvailable);
                            userPairStateService.modalsAvailable = payload.modalsAvailable;
                        }

                        if (helperService.notNullOrUndefined(payload.askToUnmuteAvailable)) {
                            logger.debug('Received askToUnmuteAvailable payload:', payload.askToUnmuteAvailable);
                            userPairStateService.askToUnmuteAvailable = payload.askToUnmuteAvailable;
                        }

                        if (helperService.notNullOrUndefined(payload.enableAudioDisconnect)) {
                            logger.debug('Received enableAudioDisconnect payload:', payload.enableAudioDisconnect);
                            meetingService.setEnableAudioDisconnect(payload.enableAudioDisconnect);
                        }

                        if (helperService.notNullOrUndefined(payload.e2eeMaxUsers)) {
                            logger.debug('Received e2eeMaxUsers payload:', payload.e2eeMaxUsers);
                            meetingService.setE2EEMaxUsers(payload.e2eeMaxUsers);
                        }

                        if (helperService.notNullOrUndefined(payload.notesRecipients)) {
                            logger.debug('Received notesRecipients payload: ', payload.notesRecipients);
                            meetingService.setNotesRecipients(payload.notesRecipients);
                        }

                        if (helperService.notNullOrUndefined(payload.enableAutoPerformanceMode)) {
                            logger.debug(
                                'Received enableAutoPerformanceMode payload: ',
                                payload.enableAutoPerformanceMode
                            );
                            meetingService.setEnableAutoPerformanceMode(payload.enableAutoPerformanceMode);
                        }

                        if (helperService.notNullOrUndefined(payload.enableOracleLiveChat)) {
                            logger.debug('Received enableOracleLiveChat payload: ', payload.enableOracleLiveChat);
                            self.enableOracleLiveChat = payload.enableOracleLiveChat;
                            meetingService.setEnableOracleLiveChat(self.enableOracleLiveChat);
                        }

                        if (helperService.notNullOrUndefined(payload.enableMarqueeButtonHide)) {
                            logger.debug('Received enableMarqueeButtonHide payload: ', payload.enableMarqueeButtonHide);
                            self.enableMarqueeButtonHide = payload.enableMarqueeButtonHide;
                            meetingService.setEnableMarqueeButtonHide(self.enableMarqueeButtonHide);
                        }

                        if (helperService.notNullOrUndefined(payload.enableCopyFirepadText)) {
                            logger.debug('Received enableCopyFirepadText payload: ', payload.enableCopyFirepadText);
                            self.enableCopyFirepadText = payload.enableCopyFirepadText;
                            meetingService.setEnableCopyFirepadText(self.enableCopyFirepadText);
                        }

                        if (helperService.notNullOrUndefined(payload.logRocketAPIKey)) {
                            logger.debug('Received enableLogRocket payload: ', payload.logRocketAPIKey);
                            if (self.shouldUseLogRocket()) {
                                $timeout(
                                    function () {
                                        $window.LogRocket.init(payload.logRocketAPIKey);
                                        $window.LogRocket.identify(attendeeService.getAttendantId(), {
                                            email: attendeeService.getEmail()
                                        });
                                    },
                                    1000,
                                    false
                                );
                            }
                        }
                    }

                    if (helperService.notNullOrUndefined(payload.denoiseSettingValue)) {
                        logger.debug('Received denoiseSettingValue:', payload.denoiseSettingValue);
                        meetingService.setDenoiseValue(payload.denoiseSettingValue);
                    }

                    if (helperService.notNullOrUndefined(payload.handRaiseAvailable)) {
                        logger.debug('Received handRaiseAvailable: ', payload.handRaiseAvailable);
                        handRaiseService.setHandRaiseAvailable(payload.handRaiseAvailable);
                    }

                    if (helperService.notNullOrUndefined(payload.echoDetectionSettingValue)) {
                        logger.debug('Received echoDetectionSettingValue:', payload.echoDetectionSettingValue);
                        meetingService.setEchoDetectionState(payload.echoDetectionSettingValue);
                    }

                    if (helperService.notNullOrUndefined(payload.autoEchoDetectionSettingValue)) {
                        logger.debug('Received autoEchoDetectionSettingValue:', payload.autoEchoDetectionSettingValue);
                        meetingService.setAutoEchoDetectionState(payload.autoEchoDetectionSettingValue);
                    }

                    if (helperService.notNullOrUndefined(payload.autoVolumeAdjustmentSettingValue)) {
                        logger.debug(
                            'Received autoVolumeAdjustmentSettingValue: ',
                            payload.autoVolumeAdjustmentSettingValue
                        );

                        meetingService.setAutoVolumeAdjustment(payload.autoVolumeAdjustmentSettingValue);
                    }

                    if (helperService.notNullOrUndefined(payload.joinBeforeHostSettingValue)) {
                        logger.debug('Received joinBeforeHostSettingValue: ', payload.joinBeforeHostSettingValue);

                        meetingService.setJoinBeforeHostEnabled(payload.joinBeforeHostSettingValue);
                    }

                    if (helperService.notNullOrUndefined(payload.recordScreenshareOnlySettingValue)) {
                        logger.debug(
                            'Received recordScreenshareOnlySettingValue: ',
                            payload.recordScreenshareOnlySettingValue
                        );

                        meetingService.setRecordScreenshareOnly(payload.recordScreenshareOnlySettingValue);
                    }

                    if (helperService.notNullOrUndefined(payload.showActiveSpeakerSettingValue)) {
                        logger.debug('Received showActiveSpeakerSettingValue: ', payload.showActiveSpeakerSettingValue);

                        meetingService.setShowActiveSpeaker(payload.showActiveSpeakerSettingValue);
                    }

                    if (helperService.notNullOrUndefined(payload.showAnnotationTooltip)) {
                        logger.debug('Received showAnnotationTooltip: ', payload.showAnnotationTooltip);

                        meetingService.setShowAnnotationTooltip(payload.showAnnotationTooltip);
                    }

                    if (helperService.notNullOrUndefined(payload.activeParticipantsHighlightsAvailable)) {
                        logger.debug(
                            'Received activeParticipantsHighlightsAvailable: ',
                            payload.activeParticipantsHighlightsAvailable
                        );

                        meetingService.setActiveParticipantsHighlightsAvailable(
                            payload.activeParticipantsHighlightsAvailable
                        );
                    }

                    if (helperService.notNullOrUndefined(payload.reducedHideAnnotationTooltipTimeAvailable)) {
                        logger.debug(
                            'Received reducedHideAnnotationTooltipTimeAvailable: ',
                            payload.reducedHideAnnotationTooltipTimeAvailable
                        );

                        meetingService.setReducedHideAnnotationTooltipTimeAvailable(
                            payload.reducedHideAnnotationTooltipTimeAvailable
                        );
                    }

                    if (helperService.notNullOrUndefined(payload.amiTranscriptionAvailable)) {
                        logger.debug('Received amiTranscriptionAvailable: ', payload.amiTranscriptionAvailable);

                        meetingService.setAmiTranscriptionAvailable(payload.amiTranscriptionAvailable);
                    }

                    if (helperService.notNullOrUndefined(payload.sendRecapToAll)) {
                        logger.debug('Received sendRecapToAll: ', payload.sendRecapToAll);
                        meetingService.setSendRecapToAll(payload.sendRecapToAll);
                    } else {
                        logger.error('retrieveInitialPayload', 'Initial payload - invalid sendRecapToAll');
                    }

                    if (helperService.notNullOrUndefined(payload.sendRecapToAllAvailable)) {
                        logger.debug('Received sendRecapToAllAvailable: ', payload.sendRecapToAllAvailable);
                        meetingService.setSendRecapToAllAvailable(payload.sendRecapToAllAvailable);
                    }

                    if (helperService.notNullOrUndefined(payload.sendMeetingRecordingToAll)) {
                        logger.debug('Received sendMeetingRecordingToAll: ', payload.sendMeetingRecordingToAll);
                        meetingService.setSendMeetingRecordingToAll(payload.sendMeetingRecordingToAll);
                    } else {
                        logger.error('retrieveInitialPayload', 'Initial payload - invalid sendMeetingRecordingToAll');
                    }

                    if (helperService.notNullOrUndefined(payload.sendMeetingRecordingToAllAvailable)) {
                        logger.debug(
                            'Received sendMeetingRecordingToAllAvailable: ',
                            payload.sendMeetingRecordingToAllAvailable
                        );
                        meetingService.setSendMeetingRecordingToAllAvailable(
                            payload.sendMeetingRecordingToAllAvailable
                        );
                    }

                    if (helperService.notNullOrUndefined(payload.enableHeapCollection)) {
                        if (payload.enableHeapCollection === true || payload.enableHeapCollection === 'true') {
                            analyticsWrapperService.initNonMobileAnalytics({
                                isPLR: brandingService.isPLR() || helperService.getQueryParameter('isPLR') === 'true',
                                shouldLoadHeap: true,
                                isHost: attendeeService.getIsHost()
                            });
                        }
                    }

                    if (helperService.isUniteApplication()) {
                        appInsightsService.loadExternal();
                    }

                    logger.info('Finished retrieving initial payload:', payload);

                    self.hasInitialPayload = true;
                    self.initActiveMeeting(payload);
                }
            });
        }

        /**
         * @memberof firebirdCtrl
         * @function getBeforeUnloadMessage
         * @description
         *   PRIVATE:
         *
         *   Returns the message to be shown during the cancelable browser event
         *   shown to the user when the window is about to unload its resources
         * @returns string beforeunload message
         */
        function getBeforeUnloadMessage() {
            var meetingTypeLower = meetingService.isWebinar() ? 'webinar' : 'meeting';

            if (attendeeService.getIsHost()) {
                return 'Leaving this page or closing this window will remove you from this ' + meetingTypeLower + '.';
            } else {
                return (
                    'Leaving this page or closing this window will remove you from this ' +
                    meetingTypeLower +
                    '.  The ' +
                    meetingTypeLower +
                    ' will continue until it is ended by the account owner.'
                );
            }
        }

        function onUserAlreadyExists(e, data) {
            if (data.attendantId !== attendeeService.getAttendantId()) {
                return;
            }

            logger.info('User already exists! Unable to log in...');

            if (helperService.isUniteApplication()) {
                routerService.setUniteDuplicateLoginState();
            } else {
                if (helperService.getIsRedesign() === true) {
                    var closeButtonHtmlEmailInUse =
                        '<button class="sweetalert-close-x" onclick="window.swal.close();" tabindex="3"><img src="img/redesign/icons/close-big.svg"></img></button>';
                    var swalOptions = {
                        title: closeButtonHtmlEmailInUse + localizationService.translate('DIALOG.EMAIL_ALREADY_IN_USE'),
                        text: localizationService.translate('DIALOG.PLEASE_USE_DIFFERENT_EMAIL'),
                        showConfirmButton: true,
                        showCancelButton: true,
                        allowOutsideClick: true,
                        confirmButtonColor: brandingService.getBaseColor(),
                        confirmButtonText: localizationService.translate('LOBBY_PAGE.DIALOG.SIGN_IN_AS_A_HOST'),
                        cancelButtonText: localizationService.translate('COMMON.CANCEL_TEXT'),
                        html: true,
                        imageUrl: 'img/redesign/modal/pop-up-pic-error.svg',
                        customClass: 'login-failure-host-modal'
                    };
                    $window.swal(swalOptions, function (retryLogin) {
                        if (retryLogin) {
                            if (helperService.getMobileHostLoggedIn()) {
                                routerService.setMobileHostAccountView();
                            } else {
                                routerService.goToAccountManager();
                            }
                        } else {
                            window.swal.close();
                        }
                    });
                } else {
                    //TODO: REMOVE POST-REDESIGN
                    $window.swal({
                        title: localizationService.translate('LOBBY_PAGE.WEBINAR_DIALOG.EMAIL_ALREADY_IN_USE.TITLE'),
                        text: localizationService.translate('LOBBY_PAGE.WEBINAR_DIALOG.EMAIL_ALREADY_IN_USE.TEXT'),
                        type: 'error',
                        showConfirmButton: true,
                        allowOutsideClick: true,
                        confirmButtonColor: brandingService.getBaseColor(),
                        confirmButtonText: localizationService.translate(
                            'LOBBY_PAGE.WEBINAR_DIALOG.EMAIL_ALREADY_IN_USE.CONFIRM'
                        )
                    });
                }

                logger.info('Redirecting back to meetingLogin');
                routerService.setMeetingLoginState({ userAlreadyExists: true }, { reload: true });
            }
        }

        function onResetOnlyOneWebAttendee() {
            $window.swal.close();
        }

        function onHostActive() {
            $window.swal.close();
        }

        function onOnlyOneWebAttendee() {
            $window.swal.close();

            $timeout(function () {
                var closeButtonHtmlAloneForHour =
                    '<button class="sweetalert-close-x" onclick="window.swal.close();" tabindex="3"><img src="img/redesign/icons/close-big.svg"></img></button>';
                var title = meetingService.isWebinar()
                    ? localizationService.translate('DIALOG.WEBINAR_ENDING')
                    : localizationService.translate('DIALOG.MEETING_ENDING');
                var text = meetingService.isWebinar()
                    ? localizationService.translate('DIALOG.ALONE_IN_WEBINAR_FOR_AN_HOUR')
                    : localizationService.translate('DIALOG.ALONE_IN_MEETING_FOR_AN_HOUR');

                $window.swal({
                    title: closeButtonHtmlAloneForHour + title,
                    text: text,
                    showConfirmButton: true,
                    allowOutsideClick: true,
                    confirmButtonColor: brandingService.getBaseColor(),
                    confirmButtonText: localizationService.translate('COMMON.OKAY_TEXT'),
                    html: true,
                    imageUrl: 'img/redesign/modal/pop-up-pic-warning.svg'
                });
            }, 1000);
        }

        function onHostInactive() {
            $window.swal.close();

            var meetingType = meetingService.isWebinar() ? 'Webinar' : 'Meeting';
            var meetingTypeLower = meetingType.toLowerCase();

            $timeout(function () {
                if (helperService.getIsRedesign() === true) {
                    var closeButtonHtmlHostAway =
                        '<button class="sweetalert-close-x" onclick="window.swal.close();" tabindex="3"><img src="img/redesign/icons/close-big.svg"></img></button>';
                    var title = meetingService.isWebinar()
                        ? 'Webinar Ending'
                        : localizationService.translate('DIALOG.MEETING_ENDING');
                    var text = meetingService.isWebinar()
                        ? 'The webinar host has been away for an hour. This webinar will end in 5 minutes.'
                        : localizationService.translate('DIALOG.HOST_AWAY_FOR_AN_HOUR');

                    $window.swal({
                        title: closeButtonHtmlHostAway + title,
                        text: text,
                        showConfirmButton: true,
                        allowOutsideClick: true,
                        confirmButtonColor: brandingService.getBaseColor(),
                        confirmButtonText: localizationService.translate('COMMON.OKAY_TEXT'),
                        html: true,
                        imageUrl: 'img/redesign/modal/pop-up-pic-warning.svg'
                    });
                } else {
                    //TODO: REMOVE POST-REDESIGN
                    $window.swal({
                        title: meetingType + ' Ending',
                        text:
                            'The ' +
                            meetingTypeLower +
                            ' host has been away for an hour. This ' +
                            meetingTypeLower +
                            ' will end in 5 minutes.',
                        showConfirmButton: true,
                        allowOutsideClick: true,
                        confirmButtonColor: brandingService.getBaseColor(),
                        confirmButtonText: 'Okay'
                    });
                }
            }, 1000);
        }

        function onRemoveOnBeforeUnloadConfirmation() {
            self.removeOnBeforeUnloadConfirmation();
        }

        function onAddOnBeforeUnloadConfirmation() {
            self.addOnBeforeUnloadConfirmation();
        }

        function onStateChangeStart(event, toState, toParams, fromState, fromParams) {
            if (toState.name.indexOf('meeting-active') > -1 === false) {
                self.removeOnBeforeUnloadConfirmation();
            }

            // needed for call setup delay calculation. do not use service or function, to have more acurate timing.
            if (!$window._appStateChanges) {
                $window._appStateChanges = [];
            }
            $window._appStateChanges.push({ from: fromState?.name, to: toState?.name, ts: Date.now() });
        }

        function onStateChangeSuccess(event, toState, toParams, fromState, fromParams) {
            if (toState.name.indexOf('meeting-active') > -1) {
                //There is an edge case in which Bootstrap dropdown doesn't get properly initiated
                //This will fix that if it happens, otherwise, this has no effect
                //
                //Timeout needed for elements in modal to finish rendering
                $timeout(function () {
                    $window.$('.dropdown-toggle').dropdown();
                }, 500);

                self.addOnBeforeUnloadConfirmation();
                self.overFlowHidden = true;

                //  we need to send notification in case meeting unlocked while attendee was waiting on approval
                if (fromState.name.indexOf('meeting-locked-waiting-for-approval') > -1) {
                    showJoinRequestAcceptedNotification();
                }
            }
        }

        function onSessionCompleted() {
            if (helperService.isUniteApplication()) {
                camaroService.onSessionCompleted();
            }

            if (helperService.isElectron() && camaroService.getIsNavigating()) {
                if (camaroService.getNavigationUrl()) {
                    $window.location.href = camaroService.getNavigationUrl();
                    return;
                }
            }

            if (attendeeService.getIsHost() === true && !helperService.isMobile()) {
                $timeout(function () {
                    // $rootScope.$broadcast('show-modal', 'meetingSurvey');
                    $rootScope.$broadcast('survey-completed');
                }, 750);
            }

            if (attendeeService.getIsAttendee() && helperService.isUniteWeb()) {
                uniteNotificationService.sendMeetingLeftEvent();
            }

            if (!helperService.getIsMeetingEnding()) {
                connectivityService.setMeetingState('ending');
                helperService.setIsMeetingEnding(true);

                //Close all popovers and alerts
                $window.$('.popover').popover('hide');
                $window.swal.close();

                if (attendeeService.getIsHost() && !helperService.isMobile()) {
                    routerService.setEndMeetingState();
                } else {
                    routerService.setMeetingAlreadyEndedState();
                }

                if (helperService.isMobile()) {
                    $window.cordova.plugins.backgroundMode.disable();
                }
            }
        }

        function setUserCacheFromQueryString() {
            if (self.isMobile) {
                return;
            }

            var name = helperService.getQueryParameter('name');
            var email = helperService.getQueryParameter('email');

            //TODO: not sure if I should set the cache if I only have one of these
            if (!name || !email) {
                return;
            }

            var userCache = {
                // TODO: [VC] Parhaps we need to use 'getMeetingIdentifier' here and in updateMeetingCodeInUserCache
                // but I don't want to break something in pre-release time. So the future hero should fix IDs in both methods.
                meetingCode: helperService.getRawMeetingIdentifier($location.absUrl()),
                user: {
                    fullname: decodeURIComponent(name),
                    email: decodeURIComponent(email)
                },
                autologin: true
            };

            cookieService.putObject('userCache', userCache, {
                expires: getTomorrowDate(),
                secure: true,
                samesite: 'strict'
            });
        }

        function getTomorrowDate() {
            var tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            return tomorrow;
        }

        function updateMeetingCodeInUserCache(meeting) {
            var userCache = cookieService.getObject('userCache');
            // TODO: [VC] Parhaps we need to use 'getMeetingIdentifier' here and in setUserCacheFromQueryString
            // but I don't want to break something in pre-release time. So the future hero should fix IDs in both methods.
            var rawMeetingId = helperService.getRawMeetingIdentifier($location.absUrl());

            // we need to check userCache is exist and it was initiated for the current request by
            // comparing meetingCode in the cache and only in this case we can update the cache content.
            if (userCache && userCache.meetingCode === rawMeetingId && meeting.meetingCode) {
                userCache.meetingCode = meeting.meetingCode;
                cookieService.putObject('userCache', userCache, {
                    expires: getTomorrowDate(),
                    secure: true,
                    samesite: 'strict'
                });
            }
        }

        function getDomainForCookiePersistence() {
            var domain = helperService.getLocationHost();
            var parts = domain.split('.').reverse();
            var result = '.' + parts[1] + '.' + parts[0];
            return result;
        }

        function onToggleLeftSidebar(e, data) {
            if (data === 'open') {
                self.sidebarToggle.left = true;
            } else if (data === 'closed') {
                self.sidebarToggle.left = false;

                var sideBarElement = $window.angular.element('#sidebar-left')[0];
                var sideBarElementComputedWidth = $window
                    .getComputedStyle(sideBarElement, null)
                    .getPropertyValue('width');
                var width = sideBarElementComputedWidth ? sideBarElementComputedWidth : '300px';
                sideBarElement.style.left = '-' + width;
            }
        }

        function onWindowError(errMsg) {
            logger.error('#onWindowError: window error', errMsg);
            analyticsWrapperService.eventTrack('Meeting - Window.onError');
        }

        function getMetaDataLink() {
            if (self.metadataLink === undefined) {
                var baseLink = brandingService.getHorizontalLogo();
                self.metadataLink =
                    helperService.getLocationProtocol() + '://meeting.' + helperService.getLocationHost() + baseLink;
            }

            return self.metadataLink;
        }

        function addAnnotationCapabilityListener() {
            if (helperService.isUniteApplication()) {
                return;
            }

            if (helperService.isElectron()) {
                $window.preloadedApi.remote.ipcRenderer.getCanHostAnnotate(function (event, data) {
                    var response = data || {};
                    logger.info('response from camaro app', JSON.stringify(response));
                    annotationService.setCanHostAnnotation(data.canHostAnnotation);
                });

                $window.preloadedApi.remote.ipcRenderer.onWindows7AeroDisabled(function () {
                    annotationService.setHasBasicAeroTheme(true);
                });
            }
        }

        function addUniteListeners() {
            if (!helperService.isUniteApplication()) {
                return;
            }

            $window.preloadedApi.remote.ipcRenderer.getSupportedFeatures(function (_, features) {
                var supportedFeatures = features || {};

                logger.info('Got supported features from uc-client: ', supportedFeatures);

                helperService.setUniteSupportedFeatures(supportedFeatures);

                if (helperService.isUniteSupportFeature(annotationService.getFeatureCode())) {
                    // issue 13440: disable annotation feature in case of Windows 7 Classic theme
                    $window.preloadedApi.remote.ipcRenderer.onWindows7AeroDisabled(function () {
                        annotationService.setHasBasicAeroTheme(true);
                        helperService.disableUniteSupportedFeature(annotationService.getFeatureCode());
                    });

                    // This call trigger Unite to send "sa-win7-aero-disabled"
                    $window.preloadedApi.remote.ipcRenderer.getCanHostAnnotate(function (event, data) {
                        // Do nothing
                    });
                }
            });

            $window.preloadedApi.remote.ipcRenderer.getSystemInfo(function (_, data) {
                var systemInfo = data || {};
                logger.info('Got system info from uc-client: ', systemInfo);
                helperService.setSystemInfo(systemInfo);
            });

            $window.preloadedApi.remote.ipcRenderer.onEndMeeting(function (_, data) {
                logger.info('received onEndMeeting from ipc', data);
                if (!attendeeService.getIsHost()) {
                    return;
                }

                camaroService.setIsMeetingInitiated();
                $rootScope.$broadcast('show-modal', 'camaroLeaveSelection', data);
            });

            $window.preloadedApi.remote.ipcRenderer.getAppInsighsContext(function (_, context) {
                appInsightsService.initialize(context);
            });

            // This is for Citrix SDK
            $window.getWindowHandleAsHex = function () {
                return $window.preloadedApi.remote.getWindowHandle();
            };

            // This is for VMware SDK
            $window.getWindowReference = function () {
                return $window.preloadedApi.remote.getWindowHandle();
            };

            $window.preloadedApi.remote.ipcRenderer.getIsVirtualizationEnvironment(function (_, data) {
                if (data.isFrameWindow !== undefined) {
                    logger.info('Show custom titlebar on virtual environment.');
                    self.shouldShowVDITitlebar = !data.isFrameWindow;
                }
            });
        }

        function initMobileRedirect(meetingObj) {
            if (meetingObj) {
                let redirectResult;

                if (meetingObj.sfuEnabled && meetingObj.meetingType !== 3) {
                    redirectResult = brandingService.getDynamicLinkRedirectUrl();
                } else {
                    // Meeting Type 3 is Webinar
                    redirectResult = brandingService.getMCURedirectUrl();
                }

                logger.info(`Mobile Redirect - Redirecting to: ${redirectResult}`);
                $window.location.href = redirectResult;
            }
        }

        function shouldUseLogRocket() {
            return true;
        }

        function digest() {
            if (!$scope.$$phase) {
                $scope.$digest();
            }
        }

        function onShowModal() {
            if (self.isCompactMode) {
                self.disableCompactMode();
            }
        }

        function onShowSwal() {
            if (self.isCompactMode) {
                self.disableCompactMode();
            }
        }

        function showRedirectionNotSupportedPopup() {
            var promptTitle = localizationService.translate('DIALOG.CANNOT_JOIN_MEETING');
            var promptMsg = localizationService.translate('DIALOG.REDIRECTION_NOT_RESPONDING');
            var closeButtonHtml =
                '<button class="sweetalert-close-x" onclick="window.swal.close();" tabindex="3"><img src="img/redesign/icons/close-big.svg"></img></button>';
            $window.swal(
                {
                    title: closeButtonHtml + promptTitle,
                    text: '<div class="sweetalert-text-fix-size sweetalert-text-style">' + promptMsg + '</div>',
                    showCancelButton: false,
                    showConfirmButton: true,
                    confirmButtonColor: brandingService.getBaseColor(),
                    confirmButtonText: localizationService.translate('HEADER.DIALOG.LEAVE_MEETING_LOWER'),
                    allowOutsideClick: false,
                    html: true,
                    imageUrl: 'img/redesign/modal/pop-up-pic-error.svg',
                    customClass: 'redirection-not-supported-modal'
                },
                function (confirmed) {
                    if (confirmed) {
                        $rootScope.$broadcast('leave-meeting-without-prompt');
                    }
                }
            );
        }

        function isTitleBarAvailable() {
            const isFullScreen = helperService.isFullScreen() || self.isFullScreen;
            return !isFullScreen && (meetingService.isTitleBarAvailable() || self.shouldShowVDITitlebar);
        }

        function getTitleBarTitle() {
            const defaultTitle = `${meetingService.getCompanyName()} Web Conferencing, Video Conferencing and Webinar Software`;
            if (!self.shouldShowMeetingTitle) {
                return defaultTitle;
            }
            const meetingTitle = meetingService.getTitle();
            return meetingTitle ? `${meetingTitle} | ${meetingService.getCompanyName()}` : defaultTitle;
        }

        function disableCompactMode() {
            $rootScope.$broadcast('enable-compact-mode', false);
        }

        function windowIsResized() {
            $rootScope.$broadcast('window-is-resized');
        }
    }
]);
